import type { ConversationMessage } from '@/features/conversations/api';
import {
  messageTransportLabels,
  smsPermanentFailure,
  smsPermanentFailureLabels,
  smsRefusalLabels,
} from '@borradh-workspace/labels';

function isOutboundMessage(role: string): boolean {
  return role === 'bot' || role === 'agent';
}

/**
 * Index of the newest message we sent. Like iMessage, only that message
 * carries the Delivered/Read receipt; failures still show on every message.
 */
export function findLastOutboundIndex(messages: ConversationMessage[]): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (isOutboundMessage(messages[index]?.role ?? '')) {
      return index;
    }
  }
  return -1;
}

/** Receipt-backed status only: rendering a bubble does not prove delivery. */
export function MessageDeliveryStatus({
  message,
  showReceipt = true,
}: { message: ConversationMessage; showReceipt?: boolean }) {
  if (message.role !== 'agent' && message.role !== 'bot') return null;
  const metadata = message.metadata as Record<string, unknown> | null;
  if (metadata?.deliveryFailure) {
    // A landline, opted-out or invalid number never takes a text: say which
    // rather than suggest a retry that can't work.
    const failure = metadata.deliveryFailure as { reason?: unknown };
    const permanent =
      typeof failure.reason === 'string'
        ? smsPermanentFailure(failure.reason)
        : null;
    return (
      <span className="text-destructive" role="status">
        Delivery failed ·{' '}
        {permanent
          ? smsPermanentFailureLabels[permanent]
          : 'check provider status before retrying'}
      </span>
    );
  }
  // Refused for good: say why rather than suggest a retry that can't work.
  const refusal =
    typeof metadata?.smsRefusal === 'string' &&
    Object.hasOwn(smsRefusalLabels, metadata.smsRefusal)
      ? smsRefusalLabels[metadata.smsRefusal as keyof typeof smsRefusalLabels]
      : null;
  if (refusal) {
    return (
      <span className="text-destructive" role="status">
        Not sent · {refusal}
      </span>
    );
  }
  if (
    metadata?.smsAcceptanceUnconfirmed ||
    metadata?.deliveryAcceptanceUnconfirmed
  ) {
    return (
      <span className="text-destructive" role="status">
        Send unconfirmed · check provider status before retrying
      </span>
    );
  }
  if (!showReceipt) return null;
  // Which transport carried it, when the provider says (LoopMessage picks
  // iMessage, then RCS, then SMS per message).
  const transport =
    typeof metadata?.transport === 'string' &&
    metadata.transport in messageTransportLabels
      ? ` via ${messageTransportLabels[metadata.transport as keyof typeof messageTransportLabels]}`
      : '';
  if (message.readAt) return <span>Read{transport}</span>;
  if (message.deliveredAt) return <span>Delivered{transport}</span>;
  return null;
}
